import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Button, Row, Col, Badge, Spinner, Alert } from 'react-bootstrap';
import { FiArrowLeft, FiMail, FiPhone, FiMapPin, FiBriefcase, FiUser, FiGlobe, FiCheckCircle, FiFileText, FiImage } from 'react-icons/fi';
import 'bootstrap/dist/css/bootstrap.css';
import { getPartnerById } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import './AdminPartnerDetail.css';

const Label = ({ children }) => (
    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{children}</div>
);

const Value = ({ children }) => (
    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{children}</div>
);

const Section = ({ title, children }) => (
    <Card className="mb-3">
        <Card.Header style={{ fontWeight: 600 }}>{title}</Card.Header>
        <Card.Body>{children}</Card.Body>
    </Card>
);

const AdminPartnerDetail = () => {
    const navigate = useNavigate();
    const { partnerId } = useParams();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [partner, setPartner] = useState(null);

    useEffect(() => {
        const fetchPartner = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await getPartnerById(partnerId);
                // Extract user object or fallback to partner/data
                const userData = data?.user || data?.partner || data?.data || data || null;
                setPartner(userData);
            } catch (e) {
                setError(handleApiError(e, 'Erreur lors du chargement du partenaire'));
            } finally {
                setLoading(false);
            }
        };
        if (partnerId) fetchPartner();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [partnerId]);

    if (loading) {
        return (
            <div className="dashboard-page admin-partner-detail-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail du partenaire</h2>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Chargement du partenaire...</p>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-page admin-partner-detail-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail du partenaire</h2>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <Alert variant="danger">{error}</Alert>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (!partner) {
        return (
            <div className="dashboard-page admin-partner-detail-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail du partenaire</h2>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <p className="text-muted">Aucune donnée de partenaire disponible pour l'identifiant: {partnerId}.</p>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    const get = (obj, path, fallback = '—') => {
        try {
            const v = path.reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj);
            return v ?? fallback;
        } catch {
            return fallback;
        }
    };

    // Helper function to format value for display
    const formatValue = (value) => {
        if (value === null || value === undefined) return '—';
        if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
        if (typeof value === 'object') return JSON.stringify(value, null, 2);
        if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : '—';
        return String(value);
    };

    // Group keys into logical sections
    const mainInfoKeys = ['first_name', 'last_name', 'company_name', 'email', 'phone_number', 'phone', 'user_code', 'code', 'role', 'activate_account', 'is_verified'];
    const addressKeys = ['user_address', 'address', 'city', 'zip_code', 'partner_zip_code', 'country', 'country_code', 'partner_country_code'];
    const financialKeys = ['commission'];
    const legalKeys = ['legal_info'];
    const documentKeys = ['logo_url', 'contract_doc_s3_link', 'contract_doc_s3_url'];
    const mfaKeys = ['mfa_enabled', 'mfa_secret', 'mfa_backup_codes'];

    const documentLabels = {
        logo_url: 'URL du logo',
        contract_doc_s3_link: 'Lien S3 documentation contractuelle',
        contract_doc_s3_url: 'URL console S3 documentation contractuelle',
    };

    const renderLinkValue = (value) => {
        const text = formatValue(value);
        if (text === '—' || !value) return text;
        const isHttp = typeof value === 'string' && /^https?:\/\//i.test(value);
        if (isHttp) {
            return (
                <a href={value} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>
                    {text}
                </a>
            );
        }
        return <span style={{ wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.9rem' }}>{text}</span>;
    };

    return (
        <div className="dashboard-page admin-partner-detail-page">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="page-title">
                    <FiBriefcase className="me-2" />
                    Détail du partenaire
                </h2>
                <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                    <FiArrowLeft className="me-2" /> Retour
                </Button>
            </div>

            {/* Informations principales */}
            <Section title="Informations principales">
                <Row>
                    {mainInfoKeys.map((key) => {
                        const value = partner[key];
                        if (value === undefined) return null;

                        let displayValue = formatValue(value);
                        let icon = null;

                        if (key === 'company_name') icon = <FiBriefcase className="me-1" />;
                        else if (key === 'first_name' || key === 'last_name') icon = <FiUser className="me-1" />;
                        else if (key === 'email') icon = <FiMail className="me-1" />;
                        else if (key === 'phone_number' || key === 'phone') icon = <FiPhone className="me-1" />;

                        if (key === 'activate_account' || key === 'is_verified') {
                            displayValue = value ? (
                                <Badge bg="success">
                                    <FiCheckCircle className="me-1" />
                                    {value ? 'Oui' : 'Non'}
                                </Badge>
                            ) : (
                                <Badge bg="secondary">Non</Badge>
                            );
                        }

                        return (
                            <Col md={4} key={key} className="mb-3">
                                <Label>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</Label>
                                <Value>
                                    {icon}
                                    {displayValue}
                                </Value>
                            </Col>
                        );
                    })}
                </Row>
            </Section>

            {/* Adresse */}
            {(addressKeys.some(key => partner && partner[key] !== undefined)) && (
                <Section title="Adresse">
                    <Row>
                        {addressKeys.map((key) => {
                            const value = partner[key];
                            if (value === undefined) return null;

                            let displayValue = formatValue(value);
                            let icon = null;

                            if (key === 'user_address' || key === 'address') {
                                icon = <FiMapPin className="me-1" />;
                                displayValue = <span style={{ whiteSpace: 'pre-line' }}>{displayValue}</span>;
                            } else if (key === 'country') {
                                icon = <FiGlobe className="me-1" />;
                            } else if (key === 'country_code' || key === 'partner_country_code') {
                                displayValue = <Badge bg="primary">{displayValue}</Badge>;
                            }

                            return (
                                <Col md={key === 'user_address' || key === 'address' ? 12 : 4} key={key} className="mb-3">
                                    <Label>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</Label>
                                    <Value>
                                        {icon}
                                        {displayValue}
                                    </Value>
                                </Col>
                            );
                        })}
                    </Row>
                </Section>
            )}

            {/* Informations financières */}
            {(financialKeys.some(key => partner && partner[key] !== undefined)) && (
                <Section title="Informations financières">
                    <Row>
                        {financialKeys.map((key) => {
                            const value = partner[key];
                            if (value === undefined) return null;

                            return (
                                <Col md={4} key={key} className="mb-3">
                                    <Label>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</Label>
                                    <Value>{formatValue(value)}</Value>
                                </Col>
                            );
                        })}
                    </Row>
                </Section>
            )}

            {/* Informations légales */}
            {(legalKeys.some(key => partner && partner[key] !== undefined)) && (
                <Section title="Informations légales">
                    <Row>
                        {legalKeys.map((key) => {
                            const value = partner[key];
                            if (value === undefined) return null;

                            return (
                                <Col md={12} key={key} className="mb-3">
                                    <Label>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</Label>
                                    <Value>
                                        <span style={{ whiteSpace: 'pre-line' }}>{formatValue(value)}</span>
                                    </Value>
                                </Col>
                            );
                        })}
                    </Row>
                </Section>
            )}

            {/* Documents */}
            {(documentKeys.some(key => partner && partner[key] !== undefined && partner[key] !== null && partner[key] !== '')) && (
                <Section title="Documents">
                    <Row>
                        {documentKeys.map((key) => {
                            const value = partner[key];
                            if (value === undefined || value === null || value === '') return null;
                            const icon = key === 'logo_url'
                                ? <FiImage className="me-1" />
                                : <FiFileText className="me-1" />;

                            return (
                                <Col md={12} key={key} className="mb-3">
                                    <Label>{documentLabels[key] || key}</Label>
                                    <Value>
                                        {icon}
                                        {renderLinkValue(value)}
                                    </Value>
                                </Col>
                            );
                        })}
                    </Row>
                </Section>
            )}

            {/* MFA */}
            {(mfaKeys.some(key => partner && partner[key] !== undefined)) && (
                <Section title="Authentification Multi-Facteurs (MFA)">
                    <Row>
                        {mfaKeys.map((key) => {
                            const value = partner[key];
                            if (value === undefined) return null;

                            let displayValue = formatValue(value);

                            if (key === 'mfa_enabled') {
                                displayValue = value ? (
                                    <Badge bg="success">
                                        <FiCheckCircle className="me-1" />
                                        Activé
                                    </Badge>
                                ) : (
                                    <Badge bg="secondary">Désactivé</Badge>
                                );
                            } else if (key === 'mfa_backup_codes' && Array.isArray(value)) {
                                displayValue = value.length > 0 ? value.join(', ') : '—';
                            }

                            return (
                                <Col md={key === 'mfa_backup_codes' ? 12 : 4} key={key} className="mb-3">
                                    <Label>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</Label>
                                    <Value>{displayValue}</Value>
                                </Col>
                            );
                        })}
                    </Row>
                </Section>
            )}

        </div>
    );
};

export default AdminPartnerDetail;

